import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatMoney, getCurrencyCode } from '@/shared/lib/format/money';
import { useAppSelector } from '@/shared/store/hooks';
import {
  useDepositChargesMutation,
  useDepositMutation,
  useVerifyPaymentMutation,
} from '../api/useWalletQueries';
import { env } from '@/shared/config/env';
import { openRazorpayCheckout } from '../lib/razorpay';

const DEFAULT_MIN = 1;
const DEFAULT_MAX = 100000;
const QUICK = [1000, 5000, 10000, 50000];

type Props = {
  open: boolean;
  onClose: () => void;
  availableBalance?: number;
  /** Prefill amount (e.g. exact campaign shortfall). */
  initialAmount?: number;
  /** Minimum allowed deposit (default 1). */
  minAmount?: number;
  /** Quick-pick amounts to offer instead of the defaults. The exact needed amount is always added. */
  quickAmounts?: number[];
  maxAmount?: number;
  title?: string;
  subtitle?: string;
  confirmLabel?: string;
  /** Called after payment is verified successfully. */
  onSuccess?: () => void;
};

type DepositBreakdownItem = {
  key?: string;
  label: string;
  type?: string;
  rate?: number;
  deduction: number;
};

type DepositChargesData = {
  depositAmount?: number;
  totalCharges?: number;
  totalPayableAmount?: number;
  breakdown: DepositBreakdownItem[];
};

function roundMoney(n: number) {
  return Math.ceil(Math.max(0, n) * 100) / 100;
}

export function DepositModal({
  open,
  onClose,
  availableBalance,
  initialAmount,
  minAmount = DEFAULT_MIN,
  quickAmounts: quickAmountOptions,
  maxAmount = DEFAULT_MAX,
  title = 'Deposit',
  subtitle,
  confirmLabel = 'Continue to pay',
  onSuccess,
}: Props) {
  const user = useAppSelector((s) => s.auth.user);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [chargesData, setChargesData] = useState<DepositChargesData | null>(null);

  const depositMut = useDepositMutation();
  const chargesMut = useDepositChargesMutation();
  const verifyMut = useVerifyPaymentMutation();
  const currency = getCurrencyCode();

  const minAllowed = Math.max(1, roundMoney(minAmount));
  const maxAllowed = Math.max(minAllowed, maxAmount);

  const needed = useMemo(() => {
    if (initialAmount == null || initialAmount <= 0) return null;
    return roundMoney(initialAmount);
  }, [initialAmount]);

  const quickAmounts = useMemo(() => {
    const set = new Set<number>(quickAmountOptions ?? QUICK);
    if (needed != null) set.add(needed);
    // An amount below the minimum could not be paid, so it is not offered.
    return [...set]
      .filter((q) => q >= minAllowed && q <= maxAllowed)
      .sort((a, b) => a - b);
  }, [needed, minAllowed, maxAllowed, quickAmountOptions]);

  useEffect(() => {
    if (!open) return;
    if (needed != null) {
      setAmount(String(needed));
      setError('');
      return;
    }
    setAmount('');
    setError('');
    setChargesData(null);
  }, [open, needed]);

  useEffect(() => {
    const n = Number(amount);
    if (!open || !n || n < minAllowed || n > maxAllowed) {
      setChargesData(null);
      return;
    }

    const t = window.setTimeout(() => {
      chargesMut.mutate(
        {
          amount: n,
          userId: (user?._id as string) || (user?.id as string),
        },
        {
          onSuccess: (res) => {
            const charges = res?.data?.charges || res?.data;
            if (!charges) {
              setChargesData(null);
              return;
            }

            const breakdown: DepositBreakdownItem[] = Array.isArray(charges.breakdown)
              ? charges.breakdown.map((item: DepositBreakdownItem) => ({
                  key: item.key,
                  label: item.label || 'Charge',
                  type: item.type,
                  rate: item.rate,
                  deduction: Number(item.deduction ?? 0),
                }))
              : [];

            setChargesData({
              depositAmount: charges.depositAmount ?? charges.requestAmount,
              totalCharges: charges.totalCharges,
              totalPayableAmount: charges.totalPayableAmount,
              breakdown,
            });
          },
          onError: () => {
            setChargesData(null);
          },
        },
      );
    }, 400);

    return () => window.clearTimeout(t);
  }, [amount, open, minAllowed, maxAllowed, user?._id, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const close = () => {
    setAmount('');
    setError('');
    setChargesData(null);
    onClose();
  };

  const num = Number(amount);

  const submit = async () => {
    if (!amount || Number(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (num > maxAllowed) {
      setError(
        `Amount cannot exceed ${currency} ${maxAllowed.toLocaleString('en-IN')}`,
      );
      return;
    }
    if (num < minAllowed) {
      setError(
        `Amount must be at least ${currency} ${minAllowed.toLocaleString('en-IN')}`,
      );
      return;
    }
    try {
      const res = await depositMut.mutateAsync({ amount: num });
      const razorpayOrder = res?.data?.razorpayOrder;
      const transaction = res?.data?.transaction;
      if (!razorpayOrder?.orderId) {
        toast.error('Could not create payment order');
        return;
      }

      const key = String(
        razorpayOrder.keyId || env.RAZORPAY_KEY || '',
      ).trim();
      if (!key) {
        toast.error(
          'Razorpay key is missing. Set VITE_RAZORPAY_KEY or configure RAZORPAY_KEY_ID on the server.',
        );
        return;
      }

      await openRazorpayCheckout({
        key,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        order_id: razorpayOrder.orderId,
        name: 'Screenista',
        description: subtitle || 'Wallet deposit',
      }).then((response) => {
        return new Promise<void>((resolve, reject) => {
          verifyMut.mutate(
            {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              transactionId: transaction?.transactionId || transaction?._id,
            },
            {
              onSuccess: () => {
                close();
                onSuccess?.();
                resolve();
              },
              onError: (err) => reject(err),
            },
          );
        });
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Payment could not be completed';
      if (message.includes('cancelled')) {
        toast.info('Payment cancelled');
      } else {
        toast.error(message);
      }
    }
  };

  const pending = depositMut.isPending || verifyMut.isPending;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={close} />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-xl border border-[var(--dash-line)] bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-[var(--dash-line)] px-5 py-4">
          <div className="min-w-0 text-left">
            <h2 className="m-0 text-base font-bold text-[var(--dash-ink)]">
              {title}
            </h2>
            {subtitle ? (
              <p className="mt-0.5 mb-0 text-xs text-[var(--dash-muted)]">
                {subtitle}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-md p-1 text-[var(--dash-muted)] hover:bg-[var(--dash-soft)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 p-5 text-left">
          <div className="rounded-lg bg-[var(--dash-soft)] px-4 py-3">
            <p className="text-xs text-[var(--dash-muted)]">Available balance</p>
            <p className="mt-0.5 text-lg font-bold text-[var(--dash-ink)]">
              {formatMoney(availableBalance)}
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-[var(--dash-muted)]">
              Amount ({currency})
            </label>
            <input
              type="number"
              inputMode="numeric"
              value={amount}
              min={1}
              max={maxAllowed}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '') {
                  setAmount('');
                  setError('');
                  return;
                }
                const numVal = Number(val);
                if (numVal > maxAllowed) {
                  setAmount(String(maxAllowed));
                  setError(
                    `Maximum deposit amount is ${currency} ${maxAllowed.toLocaleString('en-IN')}`,
                  );
                  return;
                }
                if (numVal < 0) {
                  setAmount('');
                  return;
                }
                setAmount(val);
                setError('');
              }}
              placeholder="Enter amount"
              className={`camp-number-input w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-[var(--dash-brand)] ${error ? 'border-red-300' : 'border-[var(--dash-line)]'
                }`}
            />
            {error ? (
              <p className="mt-1 text-[11px] text-red-500">{error}</p>
            ) : null}
            <p className="mt-1 text-[11px] text-[var(--dash-muted)]">
              {needed != null
                ? `Required for this campaign: ${formatMoney(needed)} · Max ${currency} ${maxAllowed.toLocaleString('en-IN')}`
                : `Max ${currency} ${maxAllowed.toLocaleString('en-IN')} allowed`}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {quickAmounts.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => {
                  setAmount(String(q));
                  setError('');
                }}
                className={`rounded-lg border py-2 text-sm font-medium hover:border-[var(--dash-brand)] ${Number(amount) === q
                  ? 'border-[var(--dash-brand)] bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]'
                  : 'border-[var(--dash-line)] text-[var(--dash-ink)]'
                  }`}
              >
                {formatMoney(q)}
              </button>
            ))}
          </div>

          {chargesMut.isPending ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-amber-900">
                  Charges breakdown
                </p>
                <div className="flex items-center gap-1.5 text-xs text-amber-800">
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-700" />
                  <span>Calculating charges…</span>
                </div>
              </div>

              {chargesData && (chargesData.breakdown.length > 0 || chargesData.totalCharges != null || chargesData.totalPayableAmount != null) ? (
                <div className="mt-2 space-y-1 opacity-50 pointer-events-none transition-opacity">
                  {chargesData.depositAmount != null ? (
                    <div className="flex justify-between py-1 text-xs text-amber-900/80">
                      <span>Deposit amount</span>
                      <span className="font-medium text-amber-950">
                        {formatMoney(chargesData.depositAmount)}
                      </span>
                    </div>
                  ) : null}

                  {chargesData.breakdown.map((item, i) => (
                    <div
                      key={item.key || i}
                      className="flex justify-between py-1 text-xs text-amber-900/80"
                    >
                      <span>
                        {item.label}
                        {item.rate != null ? ` (${item.rate}%)` : ''}
                      </span>
                      <span className="font-medium text-amber-900">
                        + {formatMoney(item.deduction)}
                      </span>
                    </div>
                  ))}

                  {chargesData.totalCharges != null ? (
                    <div className="mt-2 flex justify-between border-t border-amber-200 pt-2 text-sm font-semibold text-amber-950">
                      <span>Total charges</span>
                      <span>{formatMoney(chargesData.totalCharges)}</span>
                    </div>
                  ) : null}

                  {chargesData.totalPayableAmount != null ? (
                    <div className="mt-1.5 flex justify-between text-sm font-bold text-amber-950">
                      <span>Total payable amount</span>
                      <span>{formatMoney(chargesData.totalPayableAmount)}</span>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : chargesData && (chargesData.breakdown.length > 0 || chargesData.totalCharges != null || chargesData.totalPayableAmount != null) ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
              <p className="mb-2 text-xs font-semibold text-amber-900">
                Charges breakdown
              </p>

              {chargesData.depositAmount != null ? (
                <div className="flex justify-between py-1 text-xs text-amber-900/80">
                  <span>Deposit amount</span>
                  <span className="font-medium text-amber-950">
                    {formatMoney(chargesData.depositAmount)}
                  </span>
                </div>
              ) : null}

              {chargesData.breakdown.map((item, i) => (
                <div
                  key={item.key || i}
                  className="flex justify-between py-1 text-xs text-amber-900/80"
                >
                  <span>
                    {item.label}
                    {item.rate != null ? ` (${item.rate}%)` : ''}
                  </span>
                  <span className="font-medium text-amber-900">
                    + {formatMoney(item.deduction)}
                  </span>
                </div>
              ))}

              {chargesData.totalCharges != null ? (
                <div className="mt-2 flex justify-between border-t border-amber-200 pt-2 text-sm font-semibold text-amber-950">
                  <span>Total charges</span>
                  <span>{formatMoney(chargesData.totalCharges)}</span>
                </div>
              ) : null}

              {chargesData.totalPayableAmount != null ? (
                <div className="mt-1.5 flex justify-between text-sm font-bold text-amber-950">
                  <span>Total payable amount</span>
                  <span>{formatMoney(chargesData.totalPayableAmount)}</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex gap-2 border-t border-[var(--dash-line)] px-5 py-4">
          <button
            type="button"
            onClick={close}
            className="flex-1 rounded-lg border border-[var(--dash-line)] py-2.5 text-sm font-semibold text-[var(--dash-ink)] hover:bg-[var(--dash-soft)]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={() => void submit()}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: 'var(--dash-brand, #003481)' }}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {pending ? 'Processing…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

